import { Button, Heading, HStack, ModalCloseButton, useToast, VStack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import InputImage from "components/Atomos/Form/Elements/InputImage";
import InputMap from "components/Atomos/Form/Elements/InputMap";
import { InputSelect } from "components/Atomos/Form/Elements/InputSelect";
import { InputText } from "components/Atomos/Form/Elements/InputText";
import { InputTextArea } from "components/Atomos/Form/Elements/InputTextArea";
import { InputTextMask } from "components/Atomos/Form/Elements/InputTextMask";
import CepField from "components/Moleculas/CepField";
import StreetTypeSelect from "components/Moleculas/StreetTypeField";
import L from 'leaflet';
import { FormComponentProps } from "models/loginForm";
import { PostForm, postSchema } from "models/post.model";
import { useState } from "react";
import { useForm } from "react-hook-form";
import masks from "utils/masks/masks";
import informativePin from '_assets/imgs/mapa/map-icon-green.png';
import essentialServicePin from '_assets/imgs/mapa/map-icon-orange.png';

type PostFormProps = FormComponentProps<PostForm & { latitude: number, longitude: number }> & {
    initialData?: Partial<PostForm>
};

const essentialServiceIcon = L.icon({
    iconUrl: essentialServicePin,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const informativeIcon = L.icon({
    iconUrl: informativePin,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const NewPostForm = ({
    initialData,
    onSuccess,
    onFailed
}: PostFormProps) => {
    const [showFullForm, setShowFullForm] = useState(initialData?.type === '2');
    const { register, control, handleSubmit, formState: { errors }, setValue } = useForm<PostForm>({
        defaultValues: initialData,
        resolver: yupResolver(postSchema)
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleSubmitClick = async (data: PostForm) => {
        try {
            setIsSubmitting(true);
            await onSuccess({
                ...data,
                latitude: data.position.latitude,
                longitude: data.position.longitude
            });
        } catch (err) {
            onFailed?.(err);
        } finally {
            setIsSubmitting(false);
        }
    }

    const toast = useToast();
    const handleErrors = () => {
        toast({
            status: 'warning',
            description: 'Existem campos preenchidos incorretamente ou estão vazios.'
        });
    }

    return (
        <VStack align="flex-start" p={8} spacing={4} w="full" as="form" onSubmit={handleSubmit(handleSubmitClick, handleErrors)}>
            <HStack>
                <Heading>
                    {!initialData?.title && "Criar "}
                    {initialData?.title && "Editar "}
                    publicação
                </Heading>
                <ModalCloseButton />
            </HStack>

            <InputMap
                name="position"
                label="Clique para selecionar um ponto no mapa"
                error={errors.position?.latitude || errors.position?.longitude}
                control={control}
                customIcon={showFullForm ? essentialServiceIcon : informativeIcon}
            />

            <InputSelect
                name="type"
                label="Tipo de publicação"
                control={control}
                error={errors.type}
                option={[
                    { value: '1', label: 'Informativo' },
                    { value: '2', label: 'Serviço Essencial' }
                ]}
                onOptionChange={(value) => {
                    setShowFullForm(value === '2');
                }}
            />
            <HStack spacing={6} w="full">
                <CepField
                    name="address.zipcode"
                    label="CEP"
                    w="full"
                    control={control}
                    error={errors?.address?.zipcode}
                    onCepInformationReceive={(location) => {
                        setValue("address.streetName", location.streetName);
                        setValue("address.neighborhood", location.neighborhood);
                        setValue("address.city", location.city);
                        setValue("address.state", location.state);
                    }}
                    isRequired
                />
                <InputText
                    name="address.state"
                    label="Estado"
                    w="full"
                    register={register}
                    error={errors?.address?.state}
                />
            </HStack>
            <HStack spacing={6} w="full">
                <InputText
                    name="address.city"
                    label="Município"
                    w="full"
                    register={register}
                    error={errors?.address?.city}
                />
                <InputText
                    name="address.neighborhood"
                    label="Bairro"
                    w="full"
                    register={register}
                    error={errors?.address?.neighborhood}
                />
            </HStack>
            <StreetTypeSelect
                name="address.streetType"
                label="Tipo de Logradouro"
                w="full"
                control={control}
                error={errors?.address?.streetType}
            />
            <InputText
                name="address.streetName"
                label="Logradouro"
                w="full"
                register={register}
                error={errors?.address?.streetName}
            />
            <InputText
                name="address.addressNumber"
                label="Número"
                w="full"
                register={register}
                error={errors?.address?.addressNumber}
            />
            <InputText
                name="address.addressComplement"
                label="Complemento"
                w="full"
                register={register}
                error={errors?.address?.addressComplement}
            />
            <InputText
                name="title"
                label="Título"
                w="full"
                register={register}
                error={errors.title}
            />
            <InputTextArea
                rows={4}
                name="description"
                label="Descrição"
                register={register}
                error={errors.description}
            />
            <InputImage
                name="photo"
                label="Imagem"
                control={control}
                error={errors.photo}
            />
            {showFullForm && (
                <>
                    <InputTextMask
                        name="phoneNumber"
                        placeholder="(00) 00000-0000"
                        label="Telefone"
                        control={control}
                        mask={masks.BRL_PHONE}
                        error={errors.phoneNumber}
                    />
                    <InputTextMask
                        name="startTime"
                        placeholder="00:00"
                        label="Horário de abertura"
                        control={control}
                        mask={masks.TIME_ONLY}
                        error={errors.startTime}
                    />
                    <InputTextMask
                        name="endTime"
                        placeholder="00:00"
                        label="Horário de encerramento"
                        control={control}
                        mask={masks.TIME_ONLY}
                        error={errors.endTime}
                    />
                </>
            )}
            <Button type="submit" isFullWidth isLoading={isSubmitting}>
                {initialData?.title && "Atualizar dados"}
                {!initialData?.title && "Criar"}
            </Button>
        </VStack>
    )
}

export default NewPostForm